// src/components/recovery/RecoveryChat.tsx - MODERN STYLING
import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../redux/store";
import {
  getRecoveryChats,
  sendRecoveryMessage,
  markAllAsRead,
  addMessage,
  getUnreadChatCount,
} from "../../redux/slices/recoveryChatSlice";
import { GlobalStyles } from "../../styles/globalStyles";
import toast from "react-hot-toast";
import {
  Send,
  Paperclip,
  X,
  Image as ImageIcon,
  File,
  DownloadCloud,
  Loader2,
  User,
  Shield,
  Check,
  AlertCircle,
  MessageSquare,
  Eye,
  Sparkles,
  Clock,
  CheckCircle,
  Upload,
  Trash2,
} from "lucide-react";

interface RecoveryChatProps {
  recoveryId: number;
  caseNumber: string;
  isAdmin: boolean;
  onClose: () => void;
  isOpen: boolean;
}

interface ChatMessage {
  id: number;
  recovery_request_id: number;
  user_id: number | null;
  admin_id: number | null;
  message: string;
  is_admin: boolean;
  attachments: any[];
  read_at: string | null;
  created_at: string;
  user?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  };
  admin?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  };
  tempId?: number;
}

// Helper to normalize API data
const normalizeChatMessage = (chat: any): ChatMessage => ({
  ...chat,
  id: Number(chat.id),
  recovery_request_id: Number(chat.recovery_request_id),
  user_id: chat.user_id ? Number(chat.user_id) : null,
  admin_id: chat.admin_id ? Number(chat.admin_id) : null,
  is_admin: chat.is_admin === "1" || chat.is_admin === true,
  attachments: Array.isArray(chat.attachments) ? chat.attachments : [],
  read_at: chat.read_at,
  created_at: chat.created_at,
  user: chat.user,
  admin: chat.admin,
});

const RecoveryChat: React.FC<RecoveryChatProps> = ({
  recoveryId,
  caseNumber,
  isAdmin: currentUserIsAdmin,
  onClose,
  isOpen,
}) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const { chats, isLoading } = useSelector(
    (state: RootState) => state.recoveryChats,
  );

  const [message, setMessage] = useState("");
  const [attachments, setAttachments] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const [localConnectionStatus, setLocalConnectionStatus] = useState<
    "checking" | "ready" | "error"
  >("checking");
  const [isLoadingComplete, setIsLoadingComplete] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const initializedRef = useRef<boolean>(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Normalize all chats
  const normalizedChats = React.useMemo(() => {
    return chats.map(normalizeChatMessage);
  }, [chats]);

  // Sort and deduplicate
  const uniqueChats = React.useMemo(() => {
    const seen = new Set<string>();
    const result: ChatMessage[] = [];

    // Sort by created_at (oldest to newest)
    const sortedChats = [...normalizedChats].sort(
      (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    );

    for (const chat of sortedChats) {
      const key = chat.id ? `id_${chat.id}` : `temp_${chat.tempId}`;
      if (!seen.has(key)) {
        seen.add(key);
        result.push(chat);
      }
    }

    return result;
  }, [normalizedChats]);

  // Listen for incoming Pusher messages
  useEffect(() => {
    const handlePusherMessage = (event: CustomEvent) => {
      const { message: chatMessage, caseId } = event.detail;

      if (caseId === recoveryId && isOpen) {
        // Normalize the incoming message
        const normalizedMessage = normalizeChatMessage(chatMessage);
        dispatch(addMessage(normalizedMessage));

        // Scroll to bottom after a short delay
        setTimeout(() => {
          if (messagesEndRef.current && isLoadingComplete) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
          }
        }, 50);
      }
    };

    window.addEventListener(
      "pusher-message-received" as any,
      handlePusherMessage,
    );
    return () => {
      window.removeEventListener(
        "pusher-message-received" as any,
        handlePusherMessage,
      );
    };
  }, [dispatch, recoveryId, isOpen, isLoadingComplete]);

  // Load ALL messages when chat opens
  useEffect(() => {
    if (isOpen && recoveryId && !initializedRef.current) {
      initializedRef.current = true;
      setLocalConnectionStatus("checking");
      setIsLoadingComplete(false);

      const loadAllChats = async () => {
        try {
          // FIRST: Hide the messages container and show loading
          if (messagesContainerRef.current) {
            messagesContainerRef.current.style.opacity = "0";
            messagesContainerRef.current.style.visibility = "hidden";
          }

          // Load ALL chats (not just page 1)
          await dispatch(
            getRecoveryChats({
              recoveryId,
              loadAll: true,
            }) as any,
          );

          // Mark all as read
          await dispatch(markAllAsRead(recoveryId) as any);

          // Set status to ready
          setLocalConnectionStatus("ready");
          setIsLoadingComplete(true);

          // Wait a tiny bit for DOM to update, then show and scroll
          setTimeout(() => {
            if (messagesContainerRef.current) {
              // Show the messages container
              messagesContainerRef.current.style.opacity = "1";
              messagesContainerRef.current.style.visibility = "visible";

              // Scroll to bottom
              if (messagesEndRef.current) {
                messagesEndRef.current.scrollIntoView({ behavior: "auto" });
              }
            }
          }, 100);
        } catch (error) {
          console.error("Failed to load chat:", error);
          toast.error("Failed to load chat messages");
          setLocalConnectionStatus("error");

          // Still show container even on error
          if (messagesContainerRef.current) {
            messagesContainerRef.current.style.opacity = "1";
            messagesContainerRef.current.style.visibility = "visible";
          }
          setIsLoadingComplete(true);
        }
      };

      loadAllChats();
    }

    // Reset on close
    if (!isOpen) {
      initializedRef.current = false;
      setLocalConnectionStatus("checking");
      setIsLoadingComplete(false);
    }
  }, [isOpen, recoveryId, dispatch]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (uniqueChats.length > 0 && isOpen && isLoadingComplete) {
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    }
  }, [uniqueChats.length, isOpen, isLoadingComplete]);

  // SIMPLIFIED: Get message type
  const getMessageType = (chat: ChatMessage): "self" | "admin" | "user" => {
    const userId = user?.id;

    // Message from current user
    if (chat.is_admin && currentUserIsAdmin && chat.admin_id === userId) {
      return "self";
    }
    if (!chat.is_admin && !currentUserIsAdmin && chat.user_id === userId) {
      return "self";
    }

    // Message from admin (when current user is regular user)
    if (chat.is_admin && !currentUserIsAdmin) {
      return "admin";
    }

    // Message from user (when current user is admin)
    if (!chat.is_admin && currentUserIsAdmin) {
      return "user";
    }

    return chat.is_admin ? "admin" : "user";
  };

  // SIMPLIFIED: Get display name
  const getSenderName = (chat: ChatMessage): string => {
    const messageType = getMessageType(chat);

    if (messageType === "self") {
      return "You";
    }

    if (messageType === "admin") {
      // Admin message (not from current user)
      if (chat.admin?.first_name) {
        const name = `${chat.admin.first_name} ${chat.admin.last_name}`;
        return currentUserIsAdmin ? `${name} (Admin)` : name;
      }
      return currentUserIsAdmin ? "Other Admin" : "Support";
    }

    // User message (not from current user)
    if (chat.user?.first_name) {
      return `${chat.user.first_name} ${chat.user.last_name}`;
    }
    return "User";
  };

  // SIMPLIFIED: Get sender icon
  const getSenderIcon = (chat: ChatMessage) => {
    const messageType = getMessageType(chat);

    if (messageType === "self") {
      return <User className="h-3 w-3" />;
    }

    if (messageType === "admin") {
      return <Shield className="h-3 w-3 text-blue-500" />;
    }

    return <User className="h-3 w-3" />;
  };

  const handleSendMessage = async () => {
    if (!message.trim() && attachments.length === 0) {
      toast.error("Please enter a message or attach a file");
      return;
    }

    if (!user) {
      toast.error("User not authenticated");
      return;
    }

    setSending(true);
    try {
      // Create optimistic update
      const tempMessageId = Date.now();
      const optimisticMessage: ChatMessage = {
        id: tempMessageId,
        recovery_request_id: recoveryId,
        user_id: currentUserIsAdmin ? null : user.id,
        admin_id: currentUserIsAdmin ? user.id : null,
        message: message.trim(),
        is_admin: currentUserIsAdmin,
        attachments: attachments.map((file) => ({
          name: file.name,
          size: file.size,
          type: file.type,
          url: URL.createObjectURL(file),
        })),
        read_at: null,
        created_at: new Date().toISOString(),
        tempId: tempMessageId,
        ...(currentUserIsAdmin
          ? {
              admin: {
                id: user.id,
                first_name: user.first_name || "",
                last_name: user.last_name || "",
                email: user.email || "",
              },
            }
          : {
              user: {
                id: user.id,
                first_name: user.first_name || "",
                last_name: user.last_name || "",
                email: user.email || "",
              },
            }),
      };

      // Normalize and add
      const normalizedOptimistic = normalizeChatMessage(optimisticMessage);
      dispatch(addMessage(normalizedOptimistic));

      // Clear inputs
      const messageToSend = message.trim();
      const attachmentsToSend = [...attachments];
      setMessage("");
      setAttachments([]);

      // Scroll to bottom
      setTimeout(() => {
        if (messagesEndRef.current && isLoadingComplete) {
          messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
      }, 50);

      // Send to server
      await dispatch(
        sendRecoveryMessage({
          recoveryId,
          message: messageToSend,
          attachments: attachmentsToSend,
        }) as any,
      );

      dispatch(getUnreadChatCount() as any);
    } catch (error) {
      console.error("❌ Failed to send message:", error);
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newFiles = Array.from(files);
    const oversizedFiles = newFiles.filter(
      (file) => file.size > 5 * 1024 * 1024,
    );
    if (oversizedFiles.length > 0) {
      toast.error("Some files exceed 5MB limit");
      return;
    }

    if (attachments.length + newFiles.length > 5) {
      toast.error("Maximum 5 attachments allowed");
      return;
    }

    setAttachments([...attachments, ...newFiles]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const isToday = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Render message content
  const renderMessageContent = (chat: ChatMessage) => {
    const messageType = getMessageType(chat);
    const senderName = getSenderName(chat);
    const senderIcon = getSenderIcon(chat);
    const isCurrentUser = messageType === "self";

    return (
      <div
        className={`flex ${isCurrentUser ? "justify-end" : "justify-start"} mb-5 group/message`}
        key={chat.id || chat.tempId}
      >
        <div
          className={`max-w-[80%] rounded-2xl p-4 transition-all duration-300 backdrop-blur-sm border ${
            isCurrentUser
              ? `${GlobalStyles.chatMessageOwn} shadow-md border-primary/20`
              : `${GlobalStyles.chatMessageOther} shadow-sm border-gray-200/50 dark:border-gray-700/50`
          } group-hover/message:shadow-lg`}
        >
          {/* Message Header */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div
                className={`p-1.5 rounded-full ${
                  isCurrentUser
                    ? "bg-white/20"
                    : "bg-gray-200/50 dark:bg-gray-700/50"
                }`}
              >
                {senderIcon}
              </div>
              <span
                className={`text-xs font-medium ${
                  isCurrentUser
                    ? "text-white/90"
                    : "text-gray-700 dark:text-gray-300"
                }`}
              >
                {senderName}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span
                className={`text-xs ${
                  isCurrentUser
                    ? "text-white/70"
                    : "text-gray-500 dark:text-gray-400"
                }`}
              >
                {formatTime(chat.created_at)}
              </span>
              {chat.read_at && isCurrentUser && (
                <CheckCircle className="h-3 w-3 text-white/80" />
              )}
            </div>
          </div>

          {/* Message Content */}
          {chat.message && (
            <p
              className={`text-sm leading-relaxed mb-3 ${
                isCurrentUser
                  ? "text-white"
                  : "text-gray-800 dark:text-gray-200"
              }`}
            >
              {chat.message}
            </p>
          )}

          {/* Attachments */}
          {chat.attachments && chat.attachments.length > 0 && (
            <div className="space-y-3">
              {chat.attachments.map((attachment: any, index: number) => (
                <div
                  key={index}
                  className={`flex items-center gap-3 p-3 rounded-xl transition-all duration-300 group/attachment ${
                    isCurrentUser
                      ? "bg-white/20 hover:bg-white/30 border-white/30"
                      : "bg-gray-100/50 dark:bg-gray-700/50 hover:bg-gray-200/50 dark:hover:bg-gray-600/50 border-gray-200/50 dark:border-gray-700/50"
                  } border`}
                >
                  <div
                    className={`p-2 rounded-lg ${
                      isCurrentUser
                        ? "bg-white/30"
                        : "bg-white dark:bg-gray-800/50"
                    }`}
                  >
                    {attachment.type?.startsWith("image/") ? (
                      <ImageIcon className="h-4 w-4 text-blue-500" />
                    ) : (
                      <File className="h-4 w-4 text-gray-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p
                      className={`text-xs font-medium truncate ${
                        isCurrentUser
                          ? "text-white"
                          : "text-gray-700 dark:text-gray-300"
                      }`}
                    >
                      {attachment.name}
                    </p>
                    <p
                      className={`text-xs ${
                        isCurrentUser
                          ? "text-white/70"
                          : "text-gray-500 dark:text-gray-400"
                      }`}
                    >
                      {(attachment.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <a
                    href={attachment.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`p-2 rounded-lg transition-all duration-300 ${
                      isCurrentUser
                        ? "hover:bg-white/30 text-white/80 hover:text-white"
                        : "hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                    }`}
                    title="Download"
                  >
                    <DownloadCloud className="h-4 w-4 transition-transform group-hover/attachment:scale-110" />
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Group messages by date
  const groupedMessages = uniqueChats.reduce(
    (groups: any, chat: ChatMessage) => {
      const date = formatDate(chat.created_at);
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(chat);
      return groups;
    },
    {},
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop with blur */}
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />

        {/* Main Chat Container */}
        <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/30 dark:border-gray-700/30 w-full max-w-4xl h-[85vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-gray-200/30 dark:border-gray-700/30">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10">
                <MessageSquare className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
                  Recovery Chat
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Case #{caseNumber} •
                  <span className="ml-2">
                    {currentUserIsAdmin
                      ? "Communicating with user"
                      : "Communicating with support"}
                  </span>
                  <span
                    className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                      localConnectionStatus === "ready"
                        ? "bg-green-500/10 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800/50"
                        : localConnectionStatus === "error"
                          ? "bg-red-500/10 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800/50"
                          : "bg-blue-500/10 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800/50"
                    }`}
                  >
                    {localConnectionStatus === "ready"
                      ? "Live"
                      : localConnectionStatus === "error"
                        ? "Connection Error"
                        : "Loading..."}
                  </span>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  dispatch(markAllAsRead(recoveryId) as any);
                  toast.success("All messages marked as read");
                }}
                className={`${GlobalStyles.iconButton} text-gray-700 dark:text-gray-300 hover:text-primary transition-all duration-200 group/read`}
                title="Mark all as read"
                disabled={!isLoadingComplete}
              >
                <Eye className="h-4 w-4 transition-transform group-hover/read:scale-110" />
              </button>
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-gray-100/50 dark:hover:bg-gray-700/50 transition-all duration-200 backdrop-blur-sm group/close"
              >
                <X className="h-5 w-5 text-gray-700 dark:text-gray-300 transition-transform group-hover/close:scale-110" />
              </button>
            </div>
          </div>

          {/* Messages Container with Loading Overlay */}
          <div className="flex-1 overflow-hidden relative">
            {/* Loading Overlay */}
            {!isLoadingComplete && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl">
                <div className="text-center">
                  <div className="relative mx-auto w-16 h-16 mb-4">
                    <div className="absolute -inset-4 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-3xl blur-xl opacity-70"></div>
                    <div className="relative w-16 h-16 rounded-full border-4 border-primary/20 border-t-primary animate-spin"></div>
                    <Sparkles className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 h-6 w-6 text-primary" />
                  </div>
                  <h4 className="text-lg font-semibold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mb-2">
                    Loading Chat
                  </h4>
                  <p className="text-gray-500 dark:text-gray-400">
                    Loading all messages, please wait
                  </p>
                </div>
              </div>
            )}

            {/* Messages Container */}
            <div
              ref={messagesContainerRef}
              className={`h-full overflow-y-auto p-5 transition-all duration-500 ${
                isLoadingComplete
                  ? "opacity-100 visible"
                  : "opacity-0 invisible"
              }`}
            >
              {uniqueChats.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center p-8">
                  <div className="mx-auto w-20 h-20 bg-gray-100/50 dark:bg-gray-800/50 rounded-full flex items-center justify-center mb-4">
                    <MessageSquare className="h-10 w-10 text-gray-400" />
                  </div>
                  <h4 className="text-xl font-semibold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mb-2">
                    No messages yet
                  </h4>
                  <p className="text-gray-500 dark:text-gray-400 max-w-md">
                    Start the conversation by sending a message. Type your
                    message below and press Enter to send.
                  </p>
                  <div className="mt-4 p-3 bg-gray-100/50 dark:bg-gray-800/50 rounded-xl">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      <Clock className="h-3 w-3 inline mr-1" />
                      Messages are encrypted and secure
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  {Object.entries(groupedMessages).map(
                    ([date, dateMessages]: [string, any]) => (
                      <div key={date}>
                        {/* Date Separator */}
                        <div className="flex items-center justify-center my-6">
                          <div className="px-4 py-1.5 bg-gray-100/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-full border border-gray-200/50 dark:border-gray-700/50">
                            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                              {isToday(date) ? "Today" : date}
                            </span>
                          </div>
                        </div>

                        {/* Messages for this date */}
                        <div className="space-y-4">
                          {dateMessages.map((chat: ChatMessage) =>
                            renderMessageContent(chat),
                          )}
                        </div>
                      </div>
                    ),
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>
          </div>

          {/* Message Input Area */}
          <div className="border-t border-gray-200/30 dark:border-gray-700/30 p-5 space-y-4">
            {/* Attachment Preview */}
            {attachments.length > 0 && (
              <div className="p-4 bg-gradient-to-br from-gray-50/50 to-gray-100/30 dark:from-gray-800/30 dark:to-gray-900/20 backdrop-blur-sm rounded-xl border border-gray-200/50 dark:border-gray-700/50">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Upload className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Attachments ({attachments.length}/5)
                    </span>
                  </div>
                  <button
                    onClick={() => setAttachments([])}
                    className="text-xs text-red-500 hover:text-red-600 transition-colors flex items-center gap-1 group/clear"
                  >
                    <Trash2 className="h-3 w-3 transition-transform group-hover/clear:scale-110" />
                    Clear all
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {attachments.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 px-3 py-2 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-lg border border-gray-200/50 dark:border-gray-700/50 group/attachment hover:border-primary/30 transition-all duration-200"
                    >
                      <div className="p-1.5 rounded bg-primary/10">
                        {file.type.startsWith("image/") ? (
                          <ImageIcon className="h-3 w-3 text-primary" />
                        ) : (
                          <File className="h-3 w-3 text-gray-500" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium truncate max-w-[120px] text-gray-700 dark:text-gray-300">
                          {file.name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {(file.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                      <button
                        onClick={() => removeAttachment(index)}
                        className="p-1 rounded hover:bg-gray-100/50 dark:hover:bg-gray-700/50 transition-colors group/remove"
                      >
                        <X className="h-3 w-3 text-gray-400 group-hover/remove:text-red-500 transition-colors" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Input Area with buttons on sides */}
            <div className="flex items-start gap-3">
              {/* Left: File Attachment Button */}
              <div className="relative flex-shrink-0">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  multiple
                  accept="image/*,.pdf,.doc,.docx,.txt"
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className={`p-3 rounded-xl border border-gray-200/50 dark:border-gray-700/50 hover:border-primary/50 transition-all duration-200 backdrop-blur-sm group/attach ${
                    !isLoadingComplete ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  title="Attach files"
                  disabled={sending || !isLoadingComplete}
                >
                  <Paperclip className="h-4 w-4 text-gray-700 dark:text-gray-300 transition-transform group-hover/attach:scale-110" />
                </button>
              </div>

              {/* Center: Message Input - Takes full available width */}
              <div className="flex-1 relative w-full">
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={
                    isLoadingComplete
                      ? "Type your message here..."
                      : "Loading chat, please wait..."
                  }
                  className={`w-full px-4 py-3 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-300 text-gray-900 dark:text-white placeholder:text-gray-500/80 dark:placeholder:text-gray-400/80 text-sm min-h-[44px] max-h-32 resize-none ${
                    !isLoadingComplete
                      ? "opacity-50 cursor-not-allowed bg-gray-100/50 dark:bg-gray-700/50"
                      : ""
                  }`}
                  disabled={sending || !isLoadingComplete}
                  rows={1}
                />
              </div>

              {/* Right: Send Button */}
              <div className="relative flex-shrink-0">
                <button
                  onClick={handleSendMessage}
                  disabled={
                    (!message.trim() && attachments.length === 0) ||
                    sending ||
                    !isLoadingComplete
                  }
                  className={`p-3 rounded-xl transition-all duration-300 backdrop-blur-sm group/send ${
                    (!message.trim() && attachments.length === 0) ||
                    sending ||
                    !isLoadingComplete
                      ? "bg-gray-300/50 dark:bg-gray-600/50 cursor-not-allowed border border-gray-200/50 dark:border-gray-700/50"
                      : `${GlobalStyles.chatSendButton} hover:shadow-lg border-0`
                  }`}
                  title={
                    !isLoadingComplete
                      ? "Please wait while chat loads..."
                      : "Send message"
                  }
                >
                  {sending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 transition-transform group-hover/send:-translate-y-0.5" />
                  )}
                </button>
              </div>
            </div>

            {/* Status Bar */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs">
                <div
                  className={`h-2 w-2 rounded-full ${
                    localConnectionStatus === "ready"
                      ? "bg-green-500 animate-pulse"
                      : localConnectionStatus === "checking"
                        ? "bg-blue-500 animate-pulse"
                        : "bg-red-500"
                  }`}
                />
                <span className="text-gray-500 dark:text-gray-400">
                  {isLoadingComplete
                    ? localConnectionStatus === "ready"
                      ? "Connected • Real-time updates"
                      : localConnectionStatus === "error"
                        ? "Connection error"
                        : "Loading..."
                    : "Loading all messages..."}
                </span>
                {sending && (
                  <span className="flex items-center gap-1 text-primary">
                    <Loader2 className="h-2 w-2 animate-spin" />
                    Sending...
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                <AlertCircle className="h-3 w-3" />
                <span>
                  Shift+Enter for new line • Max 5 attachments (5MB each)
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecoveryChat;
